import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Sparkles, AlertTriangle,
  XCircle, Download, ChevronRight, Eye, X, Layout, Lock,
} from "lucide-react";
import ResumeTemplatePicker from "../components/resume/ResumeTemplatePicker";
import UpgradeModal from "../components/UpgradeModal";
import { PageHeader, Card, Button, Input, EmptyState, Loading } from "../components/kit";

const TEMPLATE_STORAGE_KEY = "resume_preferred_template";
const MAX_RESUMES = 5;

function DownloadButton({ resumeId, variant, label }) {
  const download = async (fmt) => {
    try {
      const res = await api.get(`/resume/saved/${resumeId}/download`, {
        params: { variant, fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resume_${variant}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="flex gap-1 flex-shrink-0">
      <button onClick={() => download("pdf")}
        className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-lg border border-border hover:bg-secondary/60 transition-colors"
        title={`Download ${label} as PDF`}>
        <Download className="w-3 h-3" /> PDF
      </button>
      <button onClick={() => download("docx")}
        className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-lg border border-border hover:bg-secondary/60 transition-colors"
        title={`Download ${label} as DOCX`}>
        <Download className="w-3 h-3" /> DOCX
      </button>
    </div>
  );
}

export default function ResumesHub() {
  const { user } = useAuth();
  const isFree = user?.plan !== "premium";

  const [resumeFile, setResumeFile] = useState(null);
  const [saveName, setSaveName] = useState("");
  const [savingNew, setSavingNew] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("resume_slot");

  const [savedResumes, setSavedResumes] = useState([]);
  const [resumesAtLimit, setResumesAtLimit] = useState(false);
  const [loading, setLoading] = useState(true);

  const [inlineEdit, setInlineEdit] = useState(null);
  const [savingRename, setSavingRename] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [templateModal, setTemplateModal] = useState(null);
  const [preferredTemplate, setPreferredTemplate] = useState(
    () => localStorage.getItem(TEMPLATE_STORAGE_KEY) || null
  );

  const handleTemplateChange = (id) => {
    setPreferredTemplate(id);
    localStorage.setItem(TEMPLATE_STORAGE_KEY, id);
  };

  const fileRef = useRef();

  const loadSavedResumes = async () => {
    try {
      const { data } = await api.get("/resume/saved");
      setSavedResumes(data.resumes);
      setResumesAtLimit(data.count >= MAX_RESUMES);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSavedResumes(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") { setPreview(null); setTemplateModal(null); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handlePreview = async (resumeId, variant, resumeName, versionLabel) => {
    setPreview({ resumeId, variant, resumeName, versionLabel, text: "" });
    setPreviewLoading(true);
    try {
      const res = await api.get(`/resume/saved/${resumeId}/download`, {
        params: { variant, fmt: "txt_raw" },
        responseType: "text",
      });
      setPreview({ resumeId, variant, resumeName, versionLabel, text: res.data });
    } catch {
      toast.error("Could not load preview");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSaveNewResume = async () => {
    if (!saveName.trim()) { toast.error("Please enter a name for this resume"); return; }
    if (!resumeFile) { toast.error("Please select a file to upload"); return; }
    if (isFree && savedResumes.length >= 1) {
      setUpgradeReason("resume_slot");
      setUpgradeOpen(true);
      return;
    }
    setSavingNew(true);
    try {
      const fd = new FormData();
      fd.append("name", saveName.trim());
      fd.append("resume_file", resumeFile);
      await api.post("/resume/saved/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadSavedResumes();
      setSaveName("");
      setResumeFile(null);
      toast.success("Resume saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.plan_limit) { setUpgradeReason("resume_slot"); setUpgradeOpen(true); return; }
      toast.error(typeof detail === "string" ? detail : "Could not save");
    } finally { setSavingNew(false); }
  };

  const startInlineEdit = (id, field, currentValue) => {
    setInlineEdit({ id, field, value: currentValue });
  };

  const handleInlineRename = async () => {
    if (!inlineEdit || !inlineEdit.value.trim()) { setInlineEdit(null); return; }
    const { id, field, value } = inlineEdit;
    setSavingRename(true);
    try {
      const body = field === "original" ? { name: value.trim() } : { optimized_name: value.trim() };
      await api.patch(`/resume/saved/${id}`, body);
      await loadSavedResumes();
      setInlineEdit(null);
      toast.success("Name updated");
    } catch {
      toast.error("Could not rename");
    } finally {
      setSavingRename(false);
    }
  };

  const handleDeleteResume = async (id) => {
    if (!window.confirm("Delete this resume and its optimised version?")) return;
    try {
      await api.delete(`/resume/saved/${id}`);
      await loadSavedResumes();
      toast.success("Resume deleted");
    } catch { toast.error("Could not delete"); }
  };

  const canSave = !!resumeFile && saveName.trim().length > 0;

  const badge = "font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-md border flex-shrink-0";

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      <PageHeader
        eyebrow="Resume Hub"
        title="My Resumes."
        subtitle="Upload and manage your resumes. Used across Mock Interviews, Resume Analyzer, and Autopilot."
      />

      {/* Free plan notice */}
      {isFree && savedResumes.length >= 1 && (
        <div className="flex items-start gap-3 rounded-xl border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-4 mb-6">
          <Lock className="w-4 h-4 text-[#7C3AED] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#5B21B6]">
            Free plan allows <strong>1 resume</strong>. Delete your current resume to upload a new one, or{" "}
            <button className="underline font-semibold" onClick={() => { setUpgradeReason("resume_slot"); setUpgradeOpen(true); }}>upgrade to Premium</button>{" "}
            for unlimited resumes.
          </div>
        </div>
      )}

      {/* Limit warning */}
      {resumesAtLimit && (
        <div className="flex items-start gap-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4 mb-6">
          <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#92400E]">Limit reached ({MAX_RESUMES}/{MAX_RESUMES}). Delete a resume to upload a new one.</p>
        </div>
      )}

      {/* Upload section */}
      {!resumesAtLimit && (
        <Card padding="p-0" className="mb-10 overflow-hidden">
          <div className="border-b border-border px-4 py-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Add new resume</span>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Resume name</label>
              <Input placeholder="e.g. Software Engineer CV" value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canSave && handleSaveNewResume()} />
            </div>

            <div>
              {resumeFile ? (
                <div className="flex items-center gap-3 rounded-xl border border-border p-4 bg-secondary/30">
                  <FileText className="w-5 h-5 text-[#0025D8] flex-shrink-0" />
                  <span className="text-sm font-mono flex-1 truncate">{resumeFile.name}</span>
                  <button onClick={() => setResumeFile(null)} className="text-muted-foreground hover:text-[#E11D48]">
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()}
                  className="w-full rounded-xl border-2 border-dashed border-border p-10 text-center text-muted-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-colors">
                  <Upload className="w-6 h-6 mx-auto mb-2" />
                  <div className="text-sm">Click to upload PDF, DOCX, or TXT</div>
                </button>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setResumeFile(file);
                    if (!saveName.trim()) {
                      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
                      setSaveName(nameWithoutExt);
                    }
                  }
                  e.target.value = "";
                }} />
              <p className="text-xs text-muted-foreground font-mono mt-2">
                Files are converted to plain text for AI processing. Formatting will not be preserved.
              </p>
            </div>

            <div className="flex justify-end pt-1">
              <Button onClick={handleSaveNewResume} disabled={savingNew || !canSave} loading={savingNew}>
                {savingNew ? "Saving…" : "Save resume"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Resume list header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-bold text-2xl text-[#0F172A]">
          Saved resumes
          <span className="font-mono text-sm font-normal text-muted-foreground ml-3">{savedResumes.length} / {MAX_RESUMES}</span>
        </h2>
        <div className="flex gap-3 text-xs font-mono text-muted-foreground">
          <Link to="/resume-analyzer" className="hover:text-[#0025D8]">Resume Analyzer →</Link>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : savedResumes.length === 0 ? (
        <EmptyState icon={FileText} title="No resumes saved yet." body="Add one above to get started." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {savedResumes.map((r) => (
            <Card key={r.resume_id} padding="p-0" className="overflow-hidden">
              {/* Card header */}
              <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex-1 min-w-0">
                  {inlineEdit?.id === r.resume_id && inlineEdit?.field === "original" ? (
                    <input className="rounded-lg border border-[#0025D8] px-2 py-0.5 text-sm font-semibold focus:outline-none w-full"
                      value={inlineEdit.value} disabled={savingRename}
                      onChange={(e) => setInlineEdit((prev) => ({ ...prev, value: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") handleInlineRename(); if (e.key === "Escape") setInlineEdit(null); }}
                      onBlur={handleInlineRename} autoFocus />
                  ) : (
                    <button className="font-semibold text-sm text-left text-[#0F172A] hover:text-[#0025D8] transition-colors truncate block w-full"
                      onClick={() => startInlineEdit(r.resume_id, "original", r.name)} title="Click to rename">
                      {r.name}
                    </button>
                  )}
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    {new Date(r.created_at).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
                <button onClick={() => handleDeleteResume(r.resume_id)}
                  className="text-muted-foreground hover:text-[#E11D48] transition-colors flex-shrink-0 p-1 -mr-1 mt-0.5" title="Delete resume">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Original row */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <span className={`${badge} border-[#0025D8]/40 text-[#0025D8]`}>Original</span>
                <div className="flex-1" />
                <button onClick={() => handlePreview(r.resume_id, "original", r.name, "Original")}
                  className="p-1 text-muted-foreground hover:text-[#0025D8] transition-colors" title="Quick text preview">
                  <Eye className="w-3.5 h-3.5" />
                </button>
                <button disabled title="Run ATS Analyzer to unlock beautiful templates"
                  className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-lg border border-dashed border-border text-muted-foreground/50 cursor-not-allowed">
                  <Lock className="w-3 h-3" /> Templates
                </button>
                <DownloadButton resumeId={r.resume_id} variant="original" label="Original" />
              </div>

              {/* ATS Optimised row */}
              {r.optimized_at ? (
                <div className="flex items-center gap-3 px-4 py-3 bg-[#7C3AED]/5">
                  <span className={`${badge} border-[#7C3AED]/40 text-[#7C3AED]`}>ATS</span>
                  <div className="flex-1 min-w-0">
                    {inlineEdit?.id === r.resume_id && inlineEdit?.field === "optimized" ? (
                      <input className="rounded-lg border border-[#7C3AED] px-2 py-0.5 text-xs font-mono focus:outline-none w-full bg-white"
                        value={inlineEdit.value} disabled={savingRename}
                        onChange={(e) => setInlineEdit((prev) => ({ ...prev, value: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") handleInlineRename(); if (e.key === "Escape") setInlineEdit(null); }}
                        onBlur={handleInlineRename} autoFocus />
                    ) : (
                      <button className="text-xs font-mono text-left hover:text-[#7C3AED] transition-colors truncate flex items-center gap-1 w-full"
                        onClick={() => startInlineEdit(r.resume_id, "optimized", r.optimized_name || "ATS Optimised")} title="Click to rename">
                        <Sparkles className="w-3 h-3 text-[#7C3AED] flex-shrink-0" />
                        {r.optimized_name || "ATS Optimised"}
                      </button>
                    )}
                  </div>
                  <button onClick={() => handlePreview(r.resume_id, "optimized", r.optimized_name || "ATS Optimised", "ATS Optimised")}
                    className="p-1 text-muted-foreground hover:text-[#7C3AED] transition-colors flex-shrink-0" title="Quick text preview">
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  {r.resume_structured ? (
                    <button onClick={() => setTemplateModal(r)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-lg border border-[#7C3AED]/50 text-[#7C3AED] hover:bg-[#7C3AED]/10 transition-colors flex-shrink-0"
                      title="Preview with templates & download PDF">
                      <Layout className="w-3 h-3" /> Templates
                    </button>
                  ) : (
                    <button disabled title="Structured data not available — re-run ATS Analyzer"
                      className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-lg border border-dashed border-border text-muted-foreground/50 cursor-not-allowed">
                      <Layout className="w-3 h-3" /> Templates
                    </button>
                  )}
                  <DownloadButton resumeId={r.resume_id} variant="optimized" label="Optimised" />
                </div>
              ) : (
                <div className="flex items-center gap-3 px-4 py-3 bg-[#7C3AED]/[0.03]">
                  <span className={`${badge} border-[#7C3AED]/20 text-[#7C3AED]/50`}>ATS</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground">No ATS version yet</span>
                    <span className="text-[10px] text-muted-foreground/60 font-mono ml-2">· Templates unlocked after optimization</span>
                  </div>
                  <Link to="/resume-analyzer" className="text-xs font-mono text-[#7C3AED] hover:underline flex items-center gap-0.5 flex-shrink-0">
                    Run Analyzer <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Template picker modal */}
      {templateModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/60" onClick={() => setTemplateModal(null)} />
          <div className="relative bg-white rounded-2xl border border-border w-full max-w-4xl my-8 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <Layout className="w-4 h-4 text-[#7C3AED] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate text-[#0F172A]">{templateModal.optimized_name || templateModal.name}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">ATS Optimised · Choose a template to preview and download</p>
                </div>
              </div>
              <button onClick={() => setTemplateModal(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <ResumeTemplatePicker
                data={templateModal.resume_structured}
                candidateName={templateModal.resume_structured?.contact?.name}
                initialTemplate={preferredTemplate}
                onTemplateChange={handleTemplateChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setPreview(null)} />
          <div className="relative bg-white rounded-2xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-sm truncate text-[#0F172A]">{preview.resumeName}</span>
                <span className={`${badge} ${preview.variant === "optimized" ? "border-[#7C3AED]/40 text-[#7C3AED]" : "border-[#0025D8]/40 text-[#0025D8]"}`}>
                  {preview.versionLabel}
                </span>
              </div>
              <button onClick={() => setPreview(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0 p-1" title="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 min-h-0 bg-[#F4F7FB]">
              {previewLoading ? (
                <div className="flex items-center justify-center h-40 font-mono text-sm text-muted-foreground">Loading…</div>
              ) : (
                <pre className="text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground">{preview.text || "No content found."}</pre>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-border flex-shrink-0 bg-white">
              <span className="font-mono text-[10px] text-muted-foreground">Download this version:</span>
              <DownloadButton resumeId={preview.resumeId} variant={preview.variant} label={preview.versionLabel} />
            </div>
          </div>
        </div>
      )}

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />
    </main>
  );
}
